// ═══════════════════════════════════════════════════════════════════
//  PENDING SLOT-PLAN JOB (AB#781)
//
//  A save answers 202 with a job id and the job runs on the server. The
//  SignalR notification that it finished is dropped for anybody not
//  connected at that moment, so the page also remembers the job it is
//  waiting on — per schedule, in localStorage — and picks the polling back
//  up when it is opened again or reloaded. Storage can be missing or full
//  (private windows, quotas), so every touch is wrapped: losing the id only
//  costs the resume, never the save.
// ═══════════════════════════════════════════════════════════════════

import type { SlotPlanTarget } from "../availabilityApi"

/** The slice of Storage used here, so tests can hand in their own. */
export type JobStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">

const PREFIX = "ams.slotPlanJob"

/** One key per schedule: a site's grid, or one host's availability at one site. */
export function pendingJobKey(target: SlotPlanTarget): string {
  return target.kind === "site"
    ? `${PREFIX}.site.${target.teamSlug}`
    : `${PREFIX}.host.${target.teamSlug}.${target.calUserId}`
}

function defaultStorage(): JobStorage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage
  } catch {
    return null
  }
}

export function readPendingJob(target: SlotPlanTarget, storage: JobStorage | null = defaultStorage()): string | null {
  try {
    const value = storage?.getItem(pendingJobKey(target)) ?? null
    return value && value.trim() !== "" ? value : null
  } catch {
    return null
  }
}

export function writePendingJob(target: SlotPlanTarget, jobId: string, storage: JobStorage | null = defaultStorage()): void {
  try {
    storage?.setItem(pendingJobKey(target), jobId)
  } catch {
    // Storage full or refused: the page still polls while it is open.
  }
}

export function clearPendingJob(target: SlotPlanTarget, storage: JobStorage | null = defaultStorage()): void {
  try {
    storage?.removeItem(pendingJobKey(target))
  } catch {
    // Nothing to do: a stale id is cleared on its next read, when the job reads as finished.
  }
}
